import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { lazy, Suspense, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router';
import { sharingApi } from '../api/endpoints';
import type { Appearance } from '../api/types';
import { useAuth, useMe } from '../auth/AuthProvider';
import { addCreatedCompany, canOpen, dashboardPath, lastCompany, rememberCompany, startPath, useCompanies, useCurrentCompany } from '../features/companies/hooks';
import { applyCompanyColors } from '../features/companies/company-colors';
import { STATUS_PAGE_URL, useUnreachable } from './status-page';
import { useMyInvitations } from '../features/companies/invitations';
import { CallLogProvider } from '../features/crm/CallLog';
import { RecordLinkChoice } from '../features/crm/RecordLinkChoice';
import { useContactQuickView } from '../features/crm/useContactQuickView';
import { useDealQuickView } from '../features/crm/useDealQuickView';
import { useTaskQuickView } from '../features/crm/useTaskQuickView';
import { useStorage } from '../features/files/storage';
import { LegalChangeBanner } from '../features/legal/LegalChangeBanner';
import { LegalFooter } from '../features/legal/LegalFooter';
import { TermsPrompt } from '../features/legal/TermsPrompt';
import { UploadPanel } from '../features/files/uploads';
import { settingsPath } from '../features/settings/shared';
import { CONTRAST_CHOICES, THEMES, useContrast, useDensity, useTheme, type ContrastChoice, type Theme } from '../theme';
import { NotificationBell } from '../features/notifications/NotificationBell';
import { choosable, languageEntry, useI18n } from '../i18n';
import { Dialog, Icon, MenuPrimitive, useScrolled, useToast } from '../ui';
import { fadeIn } from '../ui/motion';
import { staysInPanes } from '../ui/panes';
import { CompanyDetailsForm } from '../features/companies/CompanyDetailsForm';
import { ModalDrawer, NavigationBar, NavigationSide, useDestinations, useFoldedDrawer } from './navigation';
import { ReadOnlyBanner } from '../features/companies/read-only';
import { useTimeZoneSync } from '../time/sync';
import { viewerZone } from '../time/viewer-zone';
import { environmentLine, openIssueCollector, useIssueCollector } from '../features/feedback/issue-collector';

// The task quick view (TF-17) is mounted here for every page, but its code (the task form, the
// task's files) comes with the first task opened.
const TaskQuickView = lazy(() => import('../features/crm/TaskQuickView').then((module) => ({ default: module.TaskQuickView })));
// The deal quick view (a deal over the Deals board or the calendar) likewise: it is the whole deal page.
const DealQuickView = lazy(() => import('../features/crm/DealPage').then((module) => ({ default: module.DealQuickView })));
// And the contact quick view (TF-373): the whole contact page, over whatever page it was opened from.
const ContactQuickView = lazy(() => import('../features/crm/ContactPage').then((module) => ({ default: module.ContactQuickView })));

export function Shell() {
  const { t } = useI18n();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const queryClient = useQueryClient();
  const claimed = useRef(false);

  // Shares addressed to my email while I was away become mine now (once per session).
  useEffect(() => {
    if (claimed.current) return;
    claimed.current = true;
    void sharingApi
      .claim()
      .then(({ claimed: count }) => {
        if (count > 0) void queryClient.invalidateQueries({ queryKey: ['shared-with-me'] });
      })
      .catch(() => undefined);
  }, [queryClient]);

  const destinations = useDestinations();
  const [folded, toggleFolded] = useFoldedDrawer();
  const [searchOpen, setSearchOpen] = useState(false);
  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);
  // Scrolled under the app bar: the bar then takes Material's level-2 surface colour (TF-87).
  const scrolled = useScrolled();
  const main = usePageFade(location.pathname);
  useCompanyColors();
  const { company } = useCurrentCompany();
  // The person's time zone, kept with their account, and the company's for suggestions (TF-220).
  useTimeZoneSync(company);
  // A computer's menu button folds the drawer to a rail; a tablet's opens the modal drawer.
  const onMenu = () => (window.matchMedia('(min-width: 840px)').matches ? toggleFolded() : setMenuOpen(true));
  const foot = <NavigationFoot />;

  return (
    <div className="app" data-drawer={folded ? 'folded' : undefined}>
      <a className="skip-link" href="#main">
        {t('nav.skip')}
      </a>
      <header className="app-header" data-scrolled={scrolled || undefined}>
        <Link to="/" className="brand" aria-label={t('app.name')}>
          <img src="/favicon.svg" alt="" />
          <span>{t('app.name')}</span>
        </Link>
        {/* Phones and tablets: the company's name as the title; its switcher is in the drawer. */}
        <span className="app-title" title={company?.name ?? t('app.name')}>
          {company?.name ?? t('app.name')}
        </span>
        <div className="app-bar-company">
          <CompanySwitcher />
        </div>
        <HeaderSearch />
        <div className="header-end">
          <button type="button" className="icon-btn search-open" aria-label={t('search.label')} aria-haspopup="dialog" onClick={() => setSearchOpen(true)}>
            <Icon name="search" />
          </button>
          <NotificationBell />
          <UserMenu />
        </div>
        <PageProgress />
      </header>
      <NavigationSide destinations={destinations} onMenu={onMenu} folded={folded} foot={foot} />
      <main ref={main} className="main" id="main" tabIndex={-1}>
        <UnreachableBanner />
        <PendingDeletionBanner />
        <ReadOnlyBanner />
        <StorageBanner />
        <LegalChangeBanner />
        <TermsPrompt />
        <CallLogProvider>
          <Outlet />
          <TaskQuickViewWhenOpen />
          <DealQuickViewWhenOpen />
          <ContactQuickViewWhenOpen />
          {/* A record opens in the side sheet or as a page, as the person chose (TF-367). */}
          <RecordLinkChoice />
        </CallLogProvider>
      </main>
      <NavigationBar destinations={destinations} onMore={() => setMenuOpen(true)} moreOpen={menuOpen} />
      <ModalDrawer destinations={destinations} open={menuOpen} onOpenChange={setMenuOpen} head={<CompanySwitcher />} foot={foot} />
      <SearchView open={searchOpen} onOpenChange={setSearchOpen} />
      <UploadPanel />
    </div>
  );
}

function TaskQuickViewWhenOpen() {
  const { taskId } = useTaskQuickView();
  if (!taskId) return null;
  return (
    <Suspense fallback={null}>
      <TaskQuickView />
    </Suspense>
  );
}

function DealQuickViewWhenOpen() {
  const { dealId } = useDealQuickView();
  if (!dealId) return null;
  return (
    <Suspense fallback={null}>
      <DealQuickView />
    </Suspense>
  );
}

function ContactQuickViewWhenOpen() {
  const { contactId } = useContactQuickView();
  if (!contactId) return null;
  return (
    <Suspense fallback={null}>
      <ContactQuickView />
    </Suspense>
  );
}

/**
 * The company's own colours (TF-109, TF-174) on every page of it, DoCuLat's elsewhere. Waits for the
 * company list: until it is known, the colours public/theme.js put in place from the last visit stay.
 */
function useCompanyColors() {
  const { tenantId, company, loading } = useCurrentCompany();
  const appearance = company?.settings?.appearance ?? null;
  const key = appearance ? JSON.stringify(appearance) : null;
  useEffect(() => {
    if (tenantId && loading) return;
    void applyCompanyColors(company ? tenantId : null, key ? (JSON.parse(key) as Appearance) : null);
  }, [tenantId, key, loading, company]);
}

/**
 * Another page (TF-89): the app bar and the navigation stay still, the page fades in (ui/motion.ts).
 * Another record beside the same list is not another page (TF-98): only its pane fades.
 */
function usePageFade(pathname: string) {
  const main = useRef<HTMLElement>(null);
  const previous = useRef<string | null>(null);
  useEffect(() => {
    const from = previous.current;
    previous.current = pathname;
    // The first page, and another record beside the same list (TF-98: only its pane fades).
    if (from === null || staysInPanes(from, pathname)) return;
    fadeIn(main.current);
  }, [pathname]);
  return main;
}

/**
 * Material's linear progress indicator under the app bar while a page loads (TF-96): whenever a
 * query has no data yet and is fetching it, after a moment so a quick load does not flash it. A
 * background refresh of something already shown does not count.
 */
function PageProgress() {
  const { t } = useI18n();
  const loading = useIsFetching({ predicate: (query) => query.state.status === 'pending' && query.state.fetchStatus === 'fetching' }) > 0;
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!loading) {
      setShown(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setShown(true), 200);
    return () => window.clearTimeout(timer);
  }, [loading]);
  return shown ? <div className="page-progress" role="progressbar" aria-label={t('common.loading')} /> : null;
}

/**
 * A request did not reach the platform (TF-133): no network, or the service down. Whether it is the
 * person's connection or DoCuLat, the status page says - it answers when DoCuLat does not. Gone with
 * the next answer.
 */
function UnreachableBanner() {
  const { t } = useI18n();
  if (!useUnreachable()) return null;
  return (
    <div className="banner banner-danger" role="alert" data-testid="unreachable-banner">
      <Icon name="warning" />
      <span>
        {t('status.unreachable')}{' '}
        <a href={STATUS_PAGE_URL} target="_blank" rel="noopener">
          {t('status.page')}
        </a>
      </span>
    </div>
  );
}

function PendingDeletionBanner() {
  const { company } = useCurrentCompany();
  const { t } = useI18n();
  if (company?.status !== 'pending_deletion') return null;
  return (
    <div className="banner banner-warning" role="status">
      <Icon name="warning" />
      {t('company.pendingDeletion')}
    </div>
  );
}

/**
 * On every page of a company, for everybody in it: storage nearly full (amber), full or not taking
 * files at all (red). Uploads are refused in those last two cases, so the reason is said where
 * everybody sees it - and the people who can do something about it get the way to do it.
 */
function StorageBanner() {
  const { company, isAdmin } = useCurrentCompany();
  const { t, bytes } = useI18n();
  const usage = useStorage(company && company.role !== 'external' ? company.id : null).data;
  if (!company || !usage) return null;
  const amounts = { used: bytes(usage.bytesUsed), quota: bytes(usage.quotaBytes), percent: String(Math.floor(usage.percentUsed)) };
  const action = isAdmin ? (
    <>
      {t('storage.freeUp')} <Link to={settingsPath(company.id, 'billing')}>{t('storage.planLink')}</Link>
    </>
  ) : (
    t('storage.askAdmin')
  );
  if (usage.uploads.reason === 'unavailable') {
    return (
      <div className="banner banner-danger" role="alert" data-testid="storage-banner" data-state="unavailable">
        <Icon name="warning" />
        <span>{t('storage.unavailable')}</span>
      </div>
    );
  }
  if (usage.state === 'full') {
    return (
      <div className="banner banner-danger" role="alert" data-testid="storage-banner" data-state="full">
        <Icon name="warning" />
        <span>
          <strong>{t('storage.full', amounts)}</strong> {action}
        </span>
      </div>
    );
  }
  if (usage.state === 'nearly_full') {
    return (
      <div className="banner banner-warning" role="status" data-testid="storage-banner" data-state="nearly_full">
        <Icon name="warning" />
        <span>
          {t('storage.nearlyFull', amounts)} {action}
        </span>
      </div>
    );
  }
  return null;
}


/** The drawer's foot: the company's storage, and the legal links. */
function NavigationFoot() {
  const { t, bytes } = useI18n();
  const { tenantId, company } = useCurrentCompany();
  const companies = useCompanies();
  const home = company ?? (tenantId ? null : lastCompany((companies.data ?? []).filter(canOpen)) ?? null);
  const usage = useStorage(home && home.role !== 'external' ? home.id : null);
  return (
    <>
      {usage.data && (
        <div className="sidebar-foot muted">
          {t('files.usage', { used: bytes(usage.data.bytesUsed), quota: bytes(usage.data.quotaBytes) })}
          <div className="usage-bar" role="presentation" data-state={usage.data.state}>
            <span style={{ width: `${Math.min(100, usage.data.percentUsed)}%` }} />
          </div>
        </div>
      )}
      <LegalFooter showVersion />
    </>
  );
}

function CompanySwitcher() {
  const { t, tp } = useI18n();
  const companies = useCompanies();
  // Invitations waiting for me, counted on the switcher (TF-78).
  const waiting = useMyInvitations().data?.invitations.length ?? 0;
  const { tenantId, company } = useCurrentCompany();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const shown = company ?? (tenantId ? null : lastCompany((companies.data ?? []).filter(canOpen)));
  const guestOf = tenantId && !company ? t('company.guest') : null;

  return (
    <>
      <MenuPrimitive.Root>
        <MenuPrimitive.Trigger className="btn btn-ghost" aria-label={waiting > 0 ? `${t('company.switch')}, ${tp('invitations.waiting', waiting)}` : t('company.switch')}>
          <Icon name="apartment" size={18} />
          <span className="company-name">{guestOf ?? shown?.name ?? t('company.switch')}</span>
          {waiting > 0 && (
            <span className="badge badge-primary" aria-hidden="true">
              {waiting}
            </span>
          )}
          <Icon name="expand_more" size={18} />
        </MenuPrimitive.Trigger>
        <MenuPrimitive.Portal>
          <MenuPrimitive.Content className="menu" align="start" sideOffset={6}>
            <div className="menu-label">{t('company.switch')}</div>
            <MenuPrimitive.Group className="menu-scroll">
              {(companies.data ?? []).map((candidate) => (
                <MenuPrimitive.Item
                  key={candidate.id}
                  className="menu-item"
                  // A closed company is listed so its people know what became of it, but not opened (TF-113).
                  disabled={!canOpen(candidate)}
                  onSelect={() => {
                    rememberCompany(candidate.id);
                    navigate(startPath(candidate));
                  }}
                >
                  {candidate.id === shown?.id ? <Icon name="check" /> : <span className="icon-space" />}
                  <span className="flex-1">{candidate.name}</span>
                  <span className="badge">{candidate.status === 'suspended' ? t('company.closed') : candidate.status === 'held' ? t('company.held') : t(`role.${candidate.role}`)}</span>
                </MenuPrimitive.Item>
              ))}
            </MenuPrimitive.Group>
            <MenuPrimitive.Separator className="menu-sep" />
            {waiting > 0 && (
              <MenuPrimitive.Item className="menu-item" onSelect={() => navigate('/?invitations')}>
                <Icon name="mail" />
                <span className="flex-1">{t('invitations.title')}</span>
                <span className="badge badge-primary">{waiting}</span>
              </MenuPrimitive.Item>
            )}
            <MenuPrimitive.Item className="menu-item" onSelect={() => setCreating(true)}>
              <Icon name="add" />
              {t('company.new')}
            </MenuPrimitive.Item>
          </MenuPrimitive.Content>
        </MenuPrimitive.Portal>
      </MenuPrimitive.Root>
      {/* Registering a company (TF-79): its name and who it is, the same form as the start page's. */}
      <Dialog open={creating} onOpenChange={setCreating} title={t('company.create')} wide>
        <CompanyDetailsForm
          mode="register"
          onCancel={() => setCreating(false)}
          onSaved={async (created) => {
            await addCreatedCompany(queryClient, created);
            setCreating(false);
            // Held while its registration is checked (TF-213): nothing to open yet; the start page says so.
            if (created.status === 'held') {
              toast.info(t('company.heldNote'));
              navigate('/');
              return;
            }
            toast.info(t('company.created', { name: created.name }));
            navigate(dashboardPath(created.id));
          }}
        />
      </Dialog>
    </>
  );
}

/** The search form's state: what is typed, and where a search goes (this company, or the last one). */
function useSearchForm(onSearched?: () => void) {
  const { tenantId } = useCurrentCompany();
  const companies = useCompanies();
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const onSearchPage = location.pathname.endsWith('/search');
  const [text, setText] = useState(onSearchPage ? (params.get('q') ?? '') : '');
  useEffect(() => {
    if (onSearchPage) setText(params.get('q') ?? '');
  }, [onSearchPage, params]);
  const target = tenantId ?? lastCompany((companies.data ?? []).filter(canOpen))?.id;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!target || !text.trim()) return;
    navigate(`/c/${target}/search?q=${encodeURIComponent(text.trim())}`);
    onSearched?.();
  };
  return { target, text, setText, submit };
}

/** A computer's search bar in the app bar (Material's search bar: 56 high, fully rounded). */
function HeaderSearch() {
  const { t } = useI18n();
  const { target, text, setText, submit } = useSearchForm();
  if (!target) return <div className="flex-1" />;
  return (
    <form className="header-search" role="search" onSubmit={submit}>
      <Icon name="search" />
      <label htmlFor="header-search" className="visually-hidden">
        {t('search.label')}
      </label>
      <input id="header-search" type="search" placeholder={t('search.placeholder')} value={text} onChange={(event) => setText(event.target.value)} />
    </form>
  );
}

/** A phone's or a tablet's search: the whole window, the keyboard up at once (Material's search view). */
function SearchView({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useI18n();
  const { target, text, setText, submit } = useSearchForm(() => onOpenChange(false));
  if (!target) return null;
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content className="search-view" aria-describedby={undefined}>
          <DialogPrimitive.Title className="visually-hidden">{t('search.title')}</DialogPrimitive.Title>
          <form className="search-view-bar" role="search" onSubmit={submit}>
            <DialogPrimitive.Close className="icon-btn" aria-label={t('common.close')}>
              <Icon name="arrow_back" />
            </DialogPrimitive.Close>
            <label htmlFor="search-view-input" className="visually-hidden">
              {t('search.label')}
            </label>
            <input id="search-view-input" type="search" autoFocus placeholder={t('search.placeholder')} value={text} onChange={(event) => setText(event.target.value)} />
          </form>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function UserMenu() {
  const me = useMe();
  const { signOut } = useAuth();
  const { t, language, setLanguage } = useI18n();
  const [theme, setTheme] = useTheme();
  const [contrast, setContrast] = useContrast();
  const [density, setDensity] = useDensity();
  const navigate = useNavigate();
  const toast = useToast();
  // Jira's issue collector, where this server has one (TF-173): loaded only when asked for.
  const collector = useIssueCollector();
  const reportProblem = () => {
    if (!collector) return;
    const environment = environmentLine({
      path: window.location.pathname,
      language,
      zone: viewerZone(),
      width: window.innerWidth,
      height: window.innerHeight,
      userAgent: navigator.userAgent,
    });
    openIssueCollector(collector, { fullname: me.name, email: me.email, environment }).catch(() => toast.error(t('feedback.unavailable')));
  };
  return (
    <MenuPrimitive.Root>
      <MenuPrimitive.Trigger className="icon-btn" aria-label={`${t('nav.account')}: ${me.name}`}>
        <Icon name="account_circle" />
      </MenuPrimitive.Trigger>
      <MenuPrimitive.Portal>
        <MenuPrimitive.Content className="menu" align="end" sideOffset={6}>
          <div className="menu-label">
            <strong className="d-block text-strong">{me.name}</strong>
            {me.email}
          </div>
          <MenuPrimitive.Item className="menu-item" onSelect={() => navigate('/account')}>
            <Icon name="account_circle" />
            {t('nav.account')}
          </MenuPrimitive.Item>
          <MenuPrimitive.Sub>
            <MenuPrimitive.SubTrigger className="menu-item">
              <Icon name="translate" />
              {t('nav.language')}
            </MenuPrimitive.SubTrigger>
            <MenuPrimitive.Portal>
              <MenuPrimitive.SubContent className="menu" sideOffset={4}>
                <MenuPrimitive.RadioGroup value={language} onValueChange={(value) => setLanguage(value as typeof language)}>
                  {choosable().map((option) => (
                    <MenuPrimitive.RadioItem key={option.code} value={option.code} className="menu-item">
                      {option.code === language ? <Icon name="check" /> : <span className="icon-space" />}
                      <span className="menu-item-text">
                        {option.name}
                        {/* Where the back end writes in another language (TF-183), said before choosing. */}
                        {option.backend !== option.code && (
                          <span className="menu-item-hint">{t('nav.languageBackend', { language: languageEntry(option.backend).name })}</span>
                        )}
                      </span>
                    </MenuPrimitive.RadioItem>
                  ))}
                </MenuPrimitive.RadioGroup>
              </MenuPrimitive.SubContent>
            </MenuPrimitive.Portal>
          </MenuPrimitive.Sub>
          <MenuPrimitive.Sub>
            <MenuPrimitive.SubTrigger className="menu-item">
              <Icon name="contrast" />
              {t('nav.appearance')}
            </MenuPrimitive.SubTrigger>
            <MenuPrimitive.Portal>
              <MenuPrimitive.SubContent className="menu" sideOffset={4}>
                <MenuPrimitive.RadioGroup value={theme} onValueChange={(value) => setTheme(value as Theme)}>
                  {THEMES.map((option) => (
                    <MenuPrimitive.RadioItem key={option} value={option} className="menu-item">
                      {option === theme ? <Icon name="check" /> : <span className="icon-space" />}
                      {t(`theme.${option}`)}
                    </MenuPrimitive.RadioItem>
                  ))}
                </MenuPrimitive.RadioGroup>
                <MenuPrimitive.Separator className="menu-sep" />
                <MenuPrimitive.Label className="menu-label">{t('contrast.label')}</MenuPrimitive.Label>
                <MenuPrimitive.RadioGroup value={contrast} onValueChange={(value) => setContrast(value as ContrastChoice)}>
                  {CONTRAST_CHOICES.map((option) => (
                    <MenuPrimitive.RadioItem key={option} value={option} className="menu-item">
                      {option === contrast ? <Icon name="check" /> : <span className="icon-space" />}
                      {t(`contrast.${option}`)}
                    </MenuPrimitive.RadioItem>
                  ))}
                </MenuPrimitive.RadioGroup>
                <MenuPrimitive.Separator className="menu-sep" />
                {/* More rows on the screen in tables (TF-95); remembered in this browser. */}
                <MenuPrimitive.CheckboxItem className="menu-item" checked={density === 'compact'} onCheckedChange={(on) => setDensity(on ? 'compact' : 'comfortable')}>
                  {density === 'compact' ? <Icon name="check" /> : <span className="icon-space" />}
                  {t('density.dense')}
                </MenuPrimitive.CheckboxItem>
              </MenuPrimitive.SubContent>
            </MenuPrimitive.Portal>
          </MenuPrimitive.Sub>
          <MenuPrimitive.Item className="menu-item" onSelect={() => window.open('/console/', '_blank', 'noopener')}>
            <Icon name="open_in_new" />
            {t('nav.console')}
          </MenuPrimitive.Item>
          {collector && (
            <MenuPrimitive.Item className="menu-item" onSelect={reportProblem}>
              <Icon name="bug_report" />
              {t('nav.reportProblem')}
            </MenuPrimitive.Item>
          )}
          <MenuPrimitive.Separator className="menu-sep" />
          <MenuPrimitive.Item className="menu-item" onSelect={() => void signOut()}>
            <Icon name="logout" />
            {t('auth.signOut')}
          </MenuPrimitive.Item>
        </MenuPrimitive.Content>
      </MenuPrimitive.Portal>
    </MenuPrimitive.Root>
  );
}
